import moment from 'moment-timezone';

const createSquare = (centerLat, centerLng, delta) => ([
  { lat: centerLat + delta, lng: centerLng - delta },
  { lat: centerLat + delta, lng: centerLng + delta },
  { lat: centerLat - delta, lng: centerLng + delta },
  { lat: centerLat - delta, lng: centerLng - delta },
]);

const COMMON_RULES = {
  sampleIntervalSec: 10,
  maxGapSamples: 3,
  debouncePoints: 2,
  dwellThresholdSec: 120,
  tripMaxDurationSec: 8 * 60 * 60,
  allowedIntermediateZoneIds: [],
  proximityEnabled: false,
  defaultProximityCutoffMeters: 30,
  proximityExitHysteresisPct: 20,
  proximityByAssetType: {},
  proximityByAssetSubType: {},
  zonePriority: {},
};

export const createStarterTemplates = (orgId = 'default-org', createdBy = 'system') => {
  const now = new Date().toISOString();

  return [
    {
      id: `tpl_transport_${Date.now()}_1`,
      name: 'Transport O-D Trips',
      status: 'ACTIVE',
      timezone: moment.tz.guess() || 'Asia/Kolkata',
      orgId,
      createdBy,
      createdAt: now,
      updatedAt: now,
      scope: {
        assetIds: [],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        locations: [],
        subLocations: [],
      },
      rules: { ...COMMON_RULES },
      thresholds: {},
      zones: [
        {
          id: 'zone_depot',
          name: 'Depot',
          zoneType: 'polygon',
          isBase: true,
          coordinates: createSquare(26.105, 91.816, 0.004),
          metadata: { color: '#1890ff' },
        },
        {
          id: 'zone_destination',
          name: 'Destination',
          zoneType: 'polygon',
          isBase: false,
          coordinates: createSquare(26.116, 91.842, 0.004),
          metadata: { color: '#52c41a' },
        },
      ],
      metrics: [
        { id: 'm_trip_dep_dst', type: 'trip_count', label: 'Trips Depot -> Destination', config: { sourceZoneId: 'zone_depot', destinationZoneId: 'zone_destination' } },
        { id: 'm_time_inside_depot', type: 'time_inside', label: 'Time Inside Depot', config: { zoneId: 'zone_depot' } },
        { id: 'm_outside_base', type: 'time_outside_base', label: 'Time Outside Base', config: { baseZoneId: 'zone_depot' } },
        { id: 'm_unique_poi', type: 'unique_poi_count', label: 'Unique POIs', config: { excludeBase: true } },
        { id: 'm_ignition_time', type: 'carry_ignition_time', label: 'Ignition Time', config: {} },
        { id: 'm_distance_run', type: 'carry_distance_run', label: 'Distance Run', config: {} },
        { id: 'm_fuel_consumed', type: 'carry_fuel_consumed', label: 'Fuel Consumed', config: {} },
      ],
    },
    {
      id: `tpl_mining_${Date.now()}_2`,
      name: 'Mining Zone Utilization',
      status: 'ACTIVE',
      timezone: moment.tz.guess() || 'Asia/Kolkata',
      orgId,
      createdBy,
      createdAt: now,
      updatedAt: now,
      scope: {
        assetIds: [],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        locations: [],
        subLocations: [],
      },
      rules: { ...COMMON_RULES },
      thresholds: {},
      zones: [
        {
          id: 'zone_mine',
          name: 'Mine Zone',
          zoneType: 'polygon',
          isBase: true,
          coordinates: createSquare(26.058, 92.048, 0.005),
          metadata: { color: '#fa8c16' },
        },
        {
          id: 'zone_crusher',
          name: 'Crusher',
          zoneType: 'polygon',
          isBase: false,
          coordinates: createSquare(26.070, 92.062, 0.004),
          metadata: { color: '#13c2c2' },
        },
      ],
      metrics: [
        { id: 'm_entries_mine', type: 'zone_entries', label: 'Mine Entries', config: { zoneId: 'zone_mine' } },
        { id: 'm_exits_mine', type: 'zone_exits', label: 'Mine Exits', config: { zoneId: 'zone_mine' } },
        { id: 'm_reentry_mine', type: 'reentry_count', label: 'Mine Re-entries', config: { zoneId: 'zone_mine' } },
        { id: 'm_time_inside_mine', type: 'time_inside', label: 'Time Inside Mine', config: { zoneId: 'zone_mine' } },
        { id: 'm_time_outside_mine', type: 'time_outside_base', label: 'Time Outside Mine', config: { baseZoneId: 'zone_mine' } },
      ],
    },
    {
      id: `tpl_aviation_${Date.now()}_3`,
      name: 'Aviation Base-Bay Circulation',
      status: 'ACTIVE',
      timezone: moment.tz.guess() || 'Asia/Kolkata',
      orgId,
      createdBy,
      createdAt: now,
      updatedAt: now,
      scope: {
        assetIds: [],
        assetTypes: [],
        assetSubTypes: [],
        assetModels: [],
        locations: [],
        subLocations: [],
      },
      rules: { ...COMMON_RULES },
      thresholds: {},
      zones: [
        {
          id: 'zone_parking',
          name: 'Parking Base',
          zoneType: 'polygon',
          isBase: true,
          coordinates: createSquare(26.103, 91.813, 0.003),
          metadata: { color: '#722ed1' },
        },
        {
          id: 'zone_bay_1',
          name: 'Bay 1',
          zoneType: 'polygon',
          isBase: false,
          coordinates: createSquare(26.100, 91.826, 0.0022),
          metadata: { color: '#eb2f96' },
        },
        {
          id: 'zone_bay_2',
          name: 'Bay 2',
          zoneType: 'polygon',
          isBase: false,
          coordinates: createSquare(26.097, 91.832, 0.0022),
          metadata: { color: '#2f54eb' },
        },
      ],
      metrics: [
        { id: 'm_multi_poi', type: 'base_to_multi_poi_visits', label: 'Base to Multi-POI Visits', config: { baseZoneId: 'zone_parking' } },
        { id: 'm_unique_poi', type: 'unique_poi_count', label: 'Unique Bays Visited', config: { excludeBase: true } },
        { id: 'm_time_inside_base', type: 'time_inside', label: 'Time at Parking', config: { zoneId: 'zone_parking' } },
        { id: 'm_time_outside_base', type: 'time_outside_base', label: 'Time Outside Parking', config: { baseZoneId: 'zone_parking' } },
      ],
    },
  ];
};

export const METRIC_TYPES = [
  { value: 'trip_count', label: 'Trip Count (A -> B)' },
  { value: 'round_trip_count', label: 'Round Trip Count (A -> B -> A)' },
  { value: 'zone_entries', label: 'Zone Entries' },
  { value: 'zone_exits', label: 'Zone Exits' },
  { value: 'reentry_count', label: 'Zone Re-entries' },
  { value: 'time_inside', label: 'Time Inside Zone' },
  { value: 'time_outside_base', label: 'Time Outside Base' },
  { value: 'base_to_multi_poi_visits', label: 'Base to Multi-POI Visits' },
  { value: 'unique_poi_count', label: 'Unique POI Count' },
  { value: 'carry_ignition_time', label: 'Carry: Ignition Time' },
  { value: 'carry_distance_run', label: 'Carry: Distance Run' },
  { value: 'carry_fuel_consumed', label: 'Carry: Fuel Consumed' },
];

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '0m';
  const safe = Math.max(0, Math.round(seconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};
